import React from "react";
import {
  Wheat, ShieldCheck, Factory, Ship, CalendarClock, ClipboardCheck,
  Wallet, Gauge, ArrowUpRight, MapPin,
} from "lucide-react";

const Kpi = ({ icon: Icon, label, value, sub, tone = "slate", testid }) => {
  const tones = {
    slate: "text-[#0A1128]",
    blue: "text-[#0052FF]",
    green: "text-[#059669]",
  };
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-slate-200/80 bg-white p-3.5 transition-colors hover:border-slate-300"
    >
      <div className="flex items-center justify-between">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-50 text-[#64748B]">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <ArrowUpRight className="h-3.5 w-3.5 text-slate-300" />
      </div>
      <p className="mt-3 text-[11px] font-medium uppercase tracking-wide text-[#94A3B8]">{label}</p>
      <p className={`mt-0.5 font-display text-lg font-bold ${tones[tone]}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-[#94A3B8]">{sub}</p>}
    </div>
  );
};

const Detail = ({ label, value, badge }) => (
  <div className="flex items-center justify-between py-2.5">
    <span className="text-[13px] text-[#64748B]">{label}</span>
    {badge ? (
      <span className="inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[11px] font-semibold text-[#059669] ring-1 ring-inset ring-[#10B981]/20">
        <ShieldCheck className="h-3 w-3" />
        {value}
      </span>
    ) : (
      <span className="text-[13px] font-semibold text-[#0A1128]">{value}</span>
    )}
  </div>
);

export default function HeroDashboard() {
  return (
    <div
      data-testid="hero-dashboard"
      className="w-full overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_40px_80px_-24px_rgba(10,17,40,0.18)]"
    >
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-5 py-3">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
            <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
          </div>
          <span className="font-mono-ui text-[11px] text-[#94A3B8]">
            demaxtore / workspace / bulkcontainer
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ECFDF5] px-2.5 py-1 text-[11px] font-semibold text-[#059669] ring-1 ring-inset ring-[#10B981]/20">
          <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" />
          Production Confirmed
        </span>
      </div>

      <div className="grid grid-cols-1 gap-5 p-5 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EBF2FF] text-[#0052FF]">
              <Wheat className="h-5 w-5" />
            </span>
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-[#94A3B8]">
                Bulk Order · BC-4821
              </p>
              <h3 className="font-display text-xl font-bold text-[#0A1128]">Wheat Flour</h3>
              <p className="mt-0.5 flex items-center gap-1 text-[12px] text-[#64748B]">
                <MapPin className="h-3 w-3" /> Kenya · FOB Mersin
              </p>
            </div>
          </div>

          <div className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-200/80 px-4">
            <Detail label="Quantity" value="500 MT" />
            <Detail label="Packaging" value="25 kg Bags" />
            <Detail label="Destination" value="Kenya" />
            <Detail label="Incoterm" value="FOB Mersin" />
            <Detail label="Supplier" value="Verified Turkish Mfr." badge />
          </div>

          <div className="mt-4 rounded-xl border border-slate-200/80 p-4">
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-medium text-[#475569]">Production Progress</span>
              <span className="font-display text-sm font-bold text-[#0052FF]">68%</span>
            </div>
            <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-gradient-to-r from-[#0052FF] to-[#3B82F6]" style={{ width: "68%" }} />
            </div>
            <div className="mt-2 flex justify-between text-[10px] font-medium text-[#94A3B8]">
              <span>Milling</span>
              <span>Packing</span>
              <span>QC</span>
              <span>Dispatch</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:col-span-3 lg:grid-cols-3">
          <Kpi icon={Factory} label="Factory Capacity" value="1,200 MT/mo" sub="42% allocated" testid="kpi-capacity" />
          <Kpi icon={Gauge} label="Production" value="68%" sub="On schedule" tone="blue" testid="kpi-production" />
          <Kpi icon={CalendarClock} label="Est. Completion" value="18 Days" sub="Apr 24" testid="kpi-completion" />
          <Kpi icon={Ship} label="Freight Status" value="Booking" sub="FreightIQ™" testid="kpi-freight" />
          <Kpi icon={ClipboardCheck} label="Inspection" value="Scheduled" sub="Pre-shipment" tone="green" testid="kpi-inspection" />
          <Kpi icon={Wallet} label="Order Value" value="$186,000" sub="Factory direct" testid="kpi-value" />
        </div>
      </div>
    </div>
  );
}
